import { inject, Injectable } from '@angular/core';
import { ApiDependencyProviderService, CollectionRequest, CollectionResponse, HttpParams } from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminBackgroundTaskStatusResponse } from '../responses/admin-background-task-status.response';
import { AdminBackgroundTaskCollectionItemResponse } from '../responses/admin-background-task-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class AdminBackgroundTaskApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    cacheable?: boolean;
    request: CollectionRequest;
  }): Observable<CollectionResponse<AdminBackgroundTaskCollectionItemResponse>> {
    const path = 'admin/system/background-task';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBackgroundTaskCollectionItemResponse,
          ),
        ),
      );
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/system/background-task/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });

    return this.provider.http.delete(url).pipe(blank());
  }

  public fetchStatus(params: {
    id: Guid;
  }): Observable<AdminBackgroundTaskStatusResponse> {
    const path = `admin/system/background-task/${HttpParams.Id}/status`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: params.id,
    });

    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBackgroundTaskStatusResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
